import { DeleteFilled } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { Button, Divider, InputNumber, Modal, Select, Space, Table, Typography } from "antd";
import { ColumnsType } from "antd/lib/table";
import { EntitySelectorRef } from "components/EntitySelector";
import PackageSelector from "components/PackageSelector";
import useAvailablePackageTypes from "hooks/useAvailablePackageTypes";
import { useAuthenticatedRequestCreator } from "hooks/useRequests";
import { StaffPackage } from "models/packages";
import { DateOnlyFormat } from "models/websiteSettings";
import moment from "moment-timezone";
import { FC, useRef, useState } from "react";

const { Text } = Typography;

enum PackageTypeFilter {
    All = 0,
    Only = 1,
    Except = 2
}

interface MoreListingsTonightModalProps {
    open: boolean;
    currentUploadCount: number;
    onCancel?: () => void;
    onConfirm?: (uploadDate: string, packageIds: string[]) => Promise<void>;
}

interface PendingCounts {
    cardCount: number;
    newUploadDate: string;
    packageIds: string[];
}
interface PendingPackage {
    id: string;
    friendlyId: number;
    customerName: string;
    count: number;
}

const usePotentialUploads = () => {
    const fetch = useAuthenticatedRequestCreator<PendingCounts, { count: number, inclusive: boolean, packageTypeIds?: string[] }>(
        ({ count, inclusive, packageTypeIds = [] }) => {
            const ptIds = packageTypeIds.length > 0
                ? `&${packageTypeIds.map(id => `packageTypeIds=${id}`).join("&")}`
                : "";

            return {
                method: "get",
                url: `/pending-listings/potential-uploads?cardCount=${count}&inclusive=${inclusive}${ptIds}`
            }
        }
    )

    const { mutateAsync: potentialUploads, isLoading } = useMutation(fetch);

    return {
        potentialUploads,
        isLoading
    };
}

const usePendingPackageUpload = () => {
    const fetch = useAuthenticatedRequestCreator<PendingPackage, number>(
        (id) => ({
            method: "get",
            url: `/pending-listings/pending-package?friendlyId=${id}`
        }), [404]
    );

    const { mutateAsync: pendingPackage, isLoading } = useMutation(fetch);

    return { pendingPackage, isLoading };
}

const MoreListingsTonightModal: FC<MoreListingsTonightModalProps> = ({
    open, currentUploadCount, onConfirm, onCancel
}) => {
    const { potentialUploads, isLoading } = usePotentialUploads();
    const { pendingPackage, isLoading: isFetchingPendingPkg } = usePendingPackageUpload();
    const [estimate, setEstimate] = useState<number | null>(null);
    const [counts, setCounts] = useState<PendingCounts | null>(null);
    const [confirming, setConfirming] = useState(false);
    const [selectedPkg, setSelectedPkg] = useState<StaffPackage | undefined>();
    const [pendingPkgs, setPendingPkgs] = useState<PendingPackage[]>([]);
    const [pkgError, setPkgError] = useState<number | undefined>();
    const { data: packageTypes = [], isLoading: isLoadingTypes } = useAvailablePackageTypes();
    const [filter, setFilter] = useState<PackageTypeFilter>(PackageTypeFilter.All);
    const [packageTypeIds, setPackageTypeIds] = useState<string[]>([]);
    const ref = useRef<EntitySelectorRef>(null);

    const working = confirming || isLoading || isFetchingPendingPkg;

    const check = async () => {
        const result = await potentialUploads({
            count: estimate!,
            inclusive: filter !== PackageTypeFilter.Except,
            packageTypeIds
        });
        setCounts(result);
    }

    const reset = () => {
        setEstimate(null);
        setCounts(null);
        setSelectedPkg(undefined);
        setPendingPkgs([]);
        setPkgError(undefined);
        setFilter(PackageTypeFilter.All);
        setPackageTypeIds([]);
        ref.current?.reset();
        if (onCancel) {
            onCancel();
        }
    }

    const confirm = async () => {
        setConfirming(true);

        try {
            // 'unioning' the counts result with any specific package ids
            const uploadDate = counts?.newUploadDate ?? moment().local().format(DateOnlyFormat);
            const pkgIds = [...(counts?.packageIds ?? []), ...pendingPkgs.map(p => p.id)]

            if (onConfirm) {
                await onConfirm(uploadDate, pkgIds);
            }

            setEstimate(null);
            setCounts(null);
        } finally {
            setConfirming(false);
        }
    }

    const appendPackage = async () => {
        setPkgError(undefined);

        try {
            const pkg = await pendingPackage(selectedPkg!.friendlyId);
            setPendingPkgs(cur => [...cur, pkg]);
        } catch {
            setPkgError(selectedPkg!.friendlyId);
        } finally {
            ref.current?.reset();
            setSelectedPkg(undefined);
        }
    }

    const columns: ColumnsType<PendingPackage> = [
        {
            render: ((_, rec) => <Button
                danger icon={<DeleteFilled />}
                onClick={() => setPendingPkgs(cur => [...cur.filter(p => p.id !== rec.id)])} />)
        },
        { title: "Package ID", dataIndex: "friendlyId" },
        { title: "Customer", dataIndex: "customerName" },
        { title: "Count", dataIndex: "count", align: 'right' }
    ];

    const filterPlaceholder = filter === PackageTypeFilter.All
        ? "Change filter to select package types"
        : "Select at least 1 package type";

    const okDisabled =
        ((counts?.cardCount ?? 0) === 0 && pendingPkgs.length === 0) ||
        (filter !== 0 && packageTypeIds.length === 0);

    return <Modal
        title="Enqueue More Listings for Tonight"
        open={open}
        closable={false}
        keyboard={false}
        maskClosable={false}
        onOk={confirm}
        okButtonProps={{ disabled: okDisabled }}
        onCancel={reset}
        confirmLoading={working}
        destroyOnClose
    >
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Text>{currentUploadCount} listings going up tonight, how many more would you like to add?</Text>
            <Space direction="vertical" style={{ width: '100%' }}>
                <Space>
                    <InputNumber value={estimate} onChange={setEstimate} min={1} />
                    <Button onClick={check} disabled={!estimate || (filter !== PackageTypeFilter.All && packageTypeIds.length === 0)}>Check</Button>
                </Space>
                <label>Package Types:</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'baseline' }}>
                    <Select
                        options={[
                            { label: 'All', value: PackageTypeFilter.All },
                            { label: 'Only', value: PackageTypeFilter.Only },
                            { label: 'Except', value: PackageTypeFilter.Except }]}
                        style={{ width: '100px' }}
                        value={filter}
                        onChange={f => {
                            setFilter(f);
                            setCounts(null);
                            if (f === PackageTypeFilter.All) {
                                setPackageTypeIds([]);
                            }
                        }}
                    />
                    <Select
                        mode="multiple"
                        placeholder={filterPlaceholder}
                        disabled={filter === PackageTypeFilter.All}
                        style={{ flex: 1 }}
                        loading={isLoadingTypes}
                        options={packageTypes.map(pt => ({ label: pt.internalName, value: pt.id }))}
                        onChange={ids => { setPackageTypeIds(ids); setCounts(null); }}
                        value={packageTypeIds}
                    />
                </div>
            </Space>
            {counts !== null && <Text>
                Found an additional {counts.cardCount.toLocaleString()} card(s) across {counts.packageIds.length.toLocaleString()} package(s),
                which would make for a total of <Text type="success" strong>{(currentUploadCount + counts.cardCount).toLocaleString()}</Text> listings going up tonight.
            </Text>}

            <Divider>And / Or</Divider>

            <Text>
                Add specific packages to be uploaded tonight
            </Text>
            <Space>
                <PackageSelector
                    ref={ref}
                    placeholder="Package ID"
                    onSelected={setSelectedPkg}
                    onClear={() => setSelectedPkg(undefined)}
                />
                <Button disabled={!selectedPkg || isLoading} onClick={appendPackage}>
                    Add
                </Button>
                {pkgError && <Text type="danger">{pkgError} has no pending uploads!</Text>}
            </Space>
            {pendingPkgs.length > 0 && <Table
                size="small"
                rowKey={r => r.id}
                columns={columns}
                dataSource={pendingPkgs}
                footer={() => <div style={{ display: 'flex', justifySelf: 'end', fontWeight: 'bold' }}>
                    {pendingPkgs.map(p => p.count).reduce((s, c) => s += c, 0)}
                </div>}
                pagination={false}
                loading={isLoading}
            />}
        </Space>
    </Modal>
}

export default MoreListingsTonightModal;